<template>
  <modal-vue
    :modal-active="modelValue"
    max-width="max-w-[1200px]"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <h2 class="text-lg font-bold text-secondary50 text-left">Histórico de aprovação de trocas</h2>
    </template>

    <div class="w-full pb-4">
      <HandoverFilters
        :model-value="handoverStore.historyFilters"
        context="history"
        @update:model-value="applyHistoryFilters"
      />
    </div>

    <div v-if="isLoadingHistory" class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg">
      <table class="w-full min-w-[1085px] table-fixed border-collapse">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="bg-[#f0f0f4] px-3 py-2.5 text-[13px] font-bold text-[#272735] text-center leading-tight align-middle"
              :class="col.width"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in 5" :key="n" class="border-t border-[#d8d8e2] bg-white animate-pulse">
            <td class="px-3 py-3">
              <div class="flex items-center gap-3">
                <div class="shrink-0 w-10 h-10 rounded-full bg-slate-200" />
                <div class="flex flex-col gap-1">
                  <div class="h-3.5 w-28 bg-slate-200 rounded" />
                  <div class="h-3 w-16 bg-slate-200 rounded" />
                </div>
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="h-3.5 w-24 bg-slate-200 rounded" />
            </td>
            <td class="px-3 py-3">
              <div class="h-3.5 w-32 bg-slate-200 rounded" />
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-3.5 w-6 bg-slate-200 rounded" />
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-3.5 w-20 bg-slate-200 rounded" />
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-3.5 w-6 bg-slate-200 rounded" />
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-3.5 w-24 bg-slate-200 rounded" />
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-6 w-20 bg-slate-200 rounded-full" />
              </div>
            </td>
            <td class="px-3 py-3">
              <div class="flex justify-center">
                <div class="h-8 w-20 bg-slate-200 rounded" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else-if="history.length > 0"
      class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg"
    >
      <table class="w-full min-w-[1085px] table-fixed border-collapse">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="bg-[#f0f0f4] px-3 py-2.5 text-[13px] font-bold text-[#272735] text-center leading-tight align-middle"
              :class="col.width"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="{ item, receiver, emptyReceiverLabel } in rows"
            :key="item.package.id"
            class="border-t border-[#d8d8e2] bg-white"
          >
            <!-- Troca: quem passou ⟶ quem recebeu -->
            <td class="px-3 py-3">
              <div class="flex items-center gap-2" data-testid="history-transfer-cell">
                <div class="min-w-0 flex-1">
                  <span class="block mb-0.5 text-[11px] font-bold uppercase text-[#7f7f8e]">
                    Passou
                  </span>
                  <DoctorInfo
                    :name="item.package.requester.name"
                    :subtitle="`CRM ${item.package.requester.crm}`"
                    :avatar-url="item.package.requester.avatarUrl"
                    :nps-score="item.package.requester.npsScore"
                    size="sm"
                    truncate
                  />
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="shrink-0 w-5 h-5 text-primary"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path :d="mdiArrowRight" fill="currentColor" />
                </svg>

                <div class="min-w-0 flex-1">
                  <template v-if="receiver">
                    <span class="block mb-0.5 text-[11px] font-bold uppercase text-[#7f7f8e]">
                      Recebeu
                    </span>
                    <DoctorInfo
                      :name="receiver.name"
                      :subtitle="`CRM ${receiver.crm}`"
                      :avatar-url="receiver.avatarUrl"
                      :nps-score="receiver.npsScore"
                      size="sm"
                      truncate
                    />
                  </template>
                  <span
                    v-else
                    class="block text-[13px] text-left text-[#7f7f8e] truncate"
                    data-testid="history-transfer-empty-receiver"
                  >
                    {{ emptyReceiverLabel }}
                  </span>
                </div>
              </div>
            </td>

            <td class="px-3 py-3">
              <span class="block text-[13px] text-[#16161d] text-left truncate">
                {{ item.package.schedule.name }}
              </span>
            </td>

            <td class="px-3 py-3">
              <div class="flex flex-col gap-0.5 overflow-hidden">
                <span class="text-[13px] font-bold text-left text-[#16161d] truncate">
                  {{ getHealthUnitNames(item)[0] ?? '-' }}
                </span>
                <span
                  v-if="getHealthUnitNames(item).length > 1"
                  class="text-[12px] text-left text-[#7f7f8e]"
                >
                  +{{ getHealthUnitNames(item).length - 1 }} unidade{{
                    getHealthUnitNames(item).length > 2 ? 's' : ''
                  }}
                </span>
              </div>
            </td>

            <td class="px-3 py-3 text-center">
              <span class="text-[13px] text-[#16161d]">{{ item.totalInQueue }}</span>
            </td>

            <td class="px-3 py-3 text-center">
              <span class="text-[13px] text-[#16161d]">
                {{ formatDate(item.package.createdAt) }}
              </span>
            </td>

            <td class="px-3 py-3 text-center">
              <span class="text-[13px] text-[#16161d]">{{ item.package.stations.length }}</span>
            </td>

            <td class="px-3 py-3 text-center">
              <span class="block text-[13px] text-[#16161d] truncate">
                {{ item.reviewerName ? getFirstAndSecondName(item.reviewerName) : '-' }}
              </span>
            </td>

            <td class="px-3 py-3 text-center">
              <div class="flex justify-center">
                <HandoverStatusBadge :status="item.package.status" />
              </div>
            </td>

            <td class="px-3 py-3 text-center">
              <OutlineButton
                text="Detalhes"
                width="auto"
                height="revert-layer"
                data-testid="history-detail-button"
                @click="handleDetail(item)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="flex items-center justify-center py-12 text-sm text-[#7f7f8e]">
      Nenhum histórico encontrado
    </div>

    <v-pagination
      v-if="!isLoadingHistory && history.length > 0 && historyMeta.totalPages > 1"
      :model-value="historyMeta.page"
      :total-visible="5"
      :length="historyMeta.totalPages"
      density="comfortable"
      color="primary"
      class="mt-4"
      data-testid="history-pagination"
      @update:model-value="handleChangePage"
    />

    <template #footer>
      <div class="flex justify-center">
        <FilledButton
          text="Fechar"
          width="100%"
          height="48px"
          class="bg-primary30"
          data-testid="history-close-button"
          @click="$emit('update:modelValue', false)"
        />
      </div>
    </template>
  </modal-vue>

  <HandoverHistoryDetailModal v-model="showDetailModal" :item="selectedItem" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { format, parseISO } from 'date-fns'
import { mdiArrowRight } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import type { AdminQueueResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import DoctorInfo from '@/components/DoctorInfo.vue'
import HandoverHistoryDetailModal from './HandoverHistoryDetailModal.vue'
import HandoverStatusBadge from './HandoverStatusBadge.vue'
import HandoverFilters from './HandoverFilters.vue'
import { resolveTransferReceiver, resolveEmptyReceiverLabel } from './handoverTransfer'
import type { HandoverFilterValues } from './handoverFilters.types'

const props = defineProps<{
  modelValue: boolean
}>()

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const handoverStore = useHandoverStore()
const { history, isLoadingHistory, historyMeta } = storeToRefs(handoverStore)

const showDetailModal = ref(false)
const selectedItem = ref<AdminQueueResponse | null>(null)

const handleDetail = (item: AdminQueueResponse) => {
  selectedItem.value = item
  showDetailModal.value = true
}

function handleChangePage(page: number) {
  handoverStore.fetchHistory(page)
}

const applyHistoryFilters = (filters: HandoverFilterValues) => {
  handoverStore.setHistoryFilters(filters)
  handoverStore.fetchHistory(1)
}

// Larguras somam 1085px (< 1200px do modal, já descontando o padding do corpo)
// para caber sem scroll horizontal a partir de 1280px de viewport.
const columns = [
  { key: 'transfer', label: 'Troca', width: 'w-[300px]' },
  { key: 'schedule', label: 'Escala', width: 'w-[110px]' },
  { key: 'healthUnit', label: 'Unidade', width: 'w-[110px]' },
  { key: 'candidates', label: 'Profissionais que querem assumir', width: 'w-[90px]' },
  { key: 'inviteDate', label: 'Data do convite', width: 'w-[95px]' },
  { key: 'shifts', label: 'Plantões', width: 'w-[70px]' },
  { key: 'sponsor', label: 'Avaliador', width: 'w-[100px]' },
  { key: 'status', label: 'Status', width: 'w-[110px]' },
  { key: 'actions', label: 'Ações', width: 'w-[100px]' }
]

// Resolver o receptor uma vez por linha, e não a cada expressão do template.
const rows = computed(() =>
  history.value.map((item) => ({
    item,
    receiver: resolveTransferReceiver(item),
    emptyReceiverLabel: resolveEmptyReceiverLabel(item)
  }))
)

// Um convite pode agrupar plantões de unidades diferentes — exibimos a primeira
// e sinalizamos as demais com um contador.
const getHealthUnitNames = (item: AdminQueueResponse) => {
  const names = item.package.stations
    .map((station) => station.healthUnit?.name)
    .filter((name): name is string => !!name)
  return [...new Set(names)]
}

const formatDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      handoverStore.resetHistoryFilters()
      handoverStore.fetchHistory(1)
    }
  }
)
</script>
